import { createSqlCanonicalEventRepositories } from "../../../../runtime/events/sql.ts";
import type { CanonicalBillingDocument } from "../../../canonical/document.ts";
import type {
	BillingEventRepository,
	BillingIngressDocumentRepository,
	BillingOutboxRepository,
} from "./repository.ts";

function jsonValue(value: unknown): string {
	return JSON.stringify(value ?? {});
}

export function createSqlBillingDocumentRepository(): BillingIngressDocumentRepository {
	return {
		async readCurrentForUpdate(tx, document) {
			if (document.kind === "payment") {
				const result = await tx.query<CanonicalBillingDocument>(
					`
SELECT 'payment' AS kind, provider, provider_payment_id AS "providerPaymentId",
  status, amount_currency, amount_value, created_at AS "createdAt", paid_at AS "paidAt"
FROM billing.billing_payments
WHERE provider = $1 AND provider_payment_id = $2
FOR UPDATE
`,
					[document.provider, document.providerPaymentId],
				);
				return result.rows[0] ?? null;
			}
			if (document.kind === "subscription") {
				const result = await tx.query<CanonicalBillingDocument>(
					`
SELECT 'subscription' AS kind, provider,
  provider_subscription_id AS "providerSubscriptionId", status
FROM billing.billing_subscriptions
WHERE provider = $1 AND provider_subscription_id = $2
FOR UPDATE
`,
					[document.provider, document.providerSubscriptionId],
				);
				return result.rows[0] ?? null;
			}
			const result = await tx.query<CanonicalBillingDocument>(
				`
SELECT 'invoice' AS kind, provider, provider_invoice_id AS "providerInvoiceId", status
FROM billing.billing_invoices
WHERE provider = $1 AND provider_invoice_id = $2
FOR UPDATE
`,
				[document.provider, document.providerInvoiceId],
			);
			return result.rows[0] ?? null;
		},
		async upsert(tx, document) {
			if (document.kind === "payment") {
				await tx.query(
					`
INSERT INTO billing.billing_payments (
  provider, provider_payment_id, provider_customer_id, provider_profile_id,
  provider_subscription_id, provider_payment_link_id, status,
  amount_currency, amount_value, description, metadata, raw, paid_at, created_at
) VALUES ($1,$2,$3,$4,$5,$6,$7,$8,$9,$10,$11::jsonb,$12::jsonb,$13,$14)
ON CONFLICT (provider, provider_payment_id) DO UPDATE SET
  status = EXCLUDED.status,
  amount_currency = EXCLUDED.amount_currency,
  amount_value = EXCLUDED.amount_value,
  metadata = EXCLUDED.metadata,
  raw = EXCLUDED.raw,
  paid_at = EXCLUDED.paid_at
`,
					[
						document.provider,
						document.providerPaymentId,
						document.providerCustomerId ?? null,
						document.providerProfileId ?? null,
						document.providerSubscriptionId ?? null,
						document.providerPaymentLinkId ?? null,
						document.status,
						document.amount.currency,
						document.amount.value,
						document.description ?? null,
						jsonValue(document.metadata),
						jsonValue(document.raw),
						document.paidAt ?? null,
						document.createdAt ?? null,
					],
				);
				return;
			}
			if (document.kind === "subscription") {
				await tx.query(
					`
INSERT INTO billing.billing_subscriptions (
  provider, provider_subscription_id, provider_customer_id, provider_profile_id,
  status, description, metadata, raw, created_at, canceled_at
) VALUES ($1,$2,$3,$4,$5,$6,$7::jsonb,$8::jsonb,$9,$10)
ON CONFLICT (provider, provider_subscription_id) DO UPDATE SET
  status = EXCLUDED.status,
  metadata = EXCLUDED.metadata,
  raw = EXCLUDED.raw,
  canceled_at = EXCLUDED.canceled_at
`,
					[
						document.provider,
						document.providerSubscriptionId,
						document.providerCustomerId,
						document.providerProfileId ?? null,
						document.status,
						document.description ?? null,
						jsonValue(document.metadata),
						jsonValue(document.raw),
						document.createdAt ?? null,
						document.canceledAt ?? null,
					],
				);
				return;
			}
			await tx.query(
				`
INSERT INTO billing.billing_invoices (
  provider, provider_invoice_id, provider_profile_id, provider_customer_id,
  status, description, metadata, raw, paid_at, created_at
) VALUES ($1,$2,$3,$4,$5,$6,$7::jsonb,$8::jsonb,$9,$10)
ON CONFLICT (provider, provider_invoice_id) DO UPDATE SET
  status = EXCLUDED.status,
  metadata = EXCLUDED.metadata,
  raw = EXCLUDED.raw,
  paid_at = EXCLUDED.paid_at
`,
				[
					document.provider,
					document.providerInvoiceId,
					document.providerProfileId ?? null,
					document.providerCustomerId ?? null,
					document.status,
					document.description ?? null,
					jsonValue(document.metadata),
					jsonValue(document.raw),
					document.paidAt ?? null,
					document.createdAt ?? null,
				],
			);
		},
	};
}

export function createSqlBillingRepositories(): {
	documents: BillingIngressDocumentRepository;
	events: BillingEventRepository;
	outbox: BillingOutboxRepository;
} {
	const ledger = createSqlCanonicalEventRepositories();
	return {
		documents: createSqlBillingDocumentRepository(),
		events: ledger.events,
		outbox: ledger.outbox,
	};
}
